import { useCallback, useState } from "react"
import { GetApp } from "../App"
import { classNames } from "../Components"
import { BlockField, BlockRow } from "../Components/Block"
import { Icon, Icons } from "../Components/Icon"
import { IShipVesselType, IShipImportStatus, Ship } from "../Types"
import { GetAppView } from "./AppView"
import { AppOverlayContent } from "./AppOverlayContentView"
import { shipSelectRow } from "./ShipSelect/Context"
import { ShipSelectViewProps } from "./ShipSelect/Params"
import { ShipSelectRowView } from "./ShipSelect/RowView"

// Re-exported so existing importers keep this entry point while the implementation
// lives in the split ShipSelect/ modules (Params + Context + the row/connect views).
export { ShipSelectViewData } from "./ShipSelect/Context"

export function ShipSelectView({ nearbyShipIds, ownShipId, ships, users, viewable }: ShipSelectViewProps) {
  const app = GetApp();
  const t = app.getT("ShipSelectView");
  const { selectShip, setAppOverlay } = GetAppView();

  // Which ship's select button is currently mid-request. `selectShip` (AppView.tsx)
  // is async and its POST /api/ship can take a while, so without this the button stays
  // clickable for the whole in-flight request and a fast double click fires two
  // concurrent selects for the same ship, racing the server (see LogbookApp.newInstance).
  const [selectingShipId, setSelectingShipId] = useState<number | undefined>(undefined);

  const handleSelect = useCallback(async (shipId: number) => {
    if (selectingShipId !== undefined) {
      return;
    }
    setSelectingShipId(shipId);
    try {
      await selectShip(shipId);
    } finally {
      setSelectingShipId(undefined);
    }
  }, [selectingShipId, selectShip]);

  // Every companion connection goes through the one connect dialog, which starts with
  // a spinner when an automatic route looks available and falls back to manual address
  // entry on failure or timeout. `nearby` only decides whether that automatic attempt
  // is worth making -- it never changes which dialog opens, so a row cannot change
  // meaning between render and click (#1279).
  const handleConnect = useCallback((shipId: number, nearby: boolean) => {
    setAppOverlay(AppOverlayContent.shipOpen, { shipId, nearby });
  }, [setAppOverlay]);

  return (
    <div className="ShipSelectView">
      {ships.map((ship) => {
        const shipUser = users?.find((u) => u.shipId == ship.id);
        const row = shipSelectRow({
          ship,
          user: shipUser,
          ownShipId,
          nearbyShipIds,
          primaryFeature: app.hasFeature("primary"),
          viewable,
        });

        if (app.cloudSession()) {
          // A cloud session has no local ship, no claim token and no LAN discovery --
          // it only ever offers a read-only view plus the options menu.
          const shipData = Ship.parse(ship).dataParsed;
          const importInProgress = !!shipData?.importStatus
            && shipData.importStatus != IShipImportStatus.complete
            && shipData.importStatus != IShipImportStatus.failed;
          return (
            <BlockRow key={ship.id}>
              <BlockField>
                <span className="ship_name_cloud">
                  <Icon icon={ship.vesselType == IShipVesselType.sailing ? Icons.SailingVessel : Icons.MotorVessel} /> {ship.name}
                </span>

                <button
                  className="button"
                  onClick={() => {
                    handleSelect(ship.id);
                  }}
                  disabled={importInProgress || selectingShipId !== undefined}
                >
                  {selectingShipId === ship.id ? t("selecting") : t("view_logbook")}
                </button>

                {app.user && app.user.accountId != undefined && (
                  <button
                    className={classNames(["button", ship.clientToken && "ship-select-button-blue"])}
                    onClick={() => setAppOverlay(AppOverlayContent.shipListOptions, { shipId: ship.id })}
                  >
                    {t("options")}
                  </button>
                )}
              </BlockField>
            </BlockRow>
          );
        }

        return (
          <ShipSelectRowView
            key={ship.id}
            row={row}
            ship={ship}
            selecting={selectingShipId === ship.id}
            selectBlocked={selectingShipId !== undefined}
            onOpen={handleSelect}
            onConnect={handleConnect}
          />
        );
      })}
    </div>
  );
}
